import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { useToast } from './ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES } from '../constants';
import { fetchWithRetry } from '../utils/tourUtils';
import { 
  ThumbsUp, 
  ThumbsDown, 
  MessageSquare, 
  CheckCircle, 
  XCircle,
  Clock,
  Camera,
  Smartphone,
  User,
  MapPin,
  Calendar,
  Eye,
  Filter,
  Search
} from 'lucide-react';

const ChecklistApprovalWorkflow = () => {
  const [checklistItems, setChecklistItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [approvalRemark, setApprovalRemark] = useState('');
  const [rejectRemark, setRejectRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [filters, setFilters] = useState({
    spaceId: '',
    userId: '',
    changeType: '',
    attachmentType: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchChecklistItems();
    const interval = setInterval(fetchChecklistItems, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [checklistItems, filters, searchTerm, activeTab]);

  const fetchChecklistItems = async () => {
    setLoading(true);
    try {
      // Get all checklist-related push queue items
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.findMany',
          payload: { 
            orderBy: { createdAt: 'desc' },
            take: 200
          }
        },
        label: 'checklist approval items'
      });

      setChecklistItems(response || []);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch checklist items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = checklistItems;

    // Tab filter
    if (activeTab === 'pending') {
      filtered = filtered.filter(item => item.pushStatus === 'PENDING');
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(item => item.approve === true);
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(item => item.reject === true);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.spaceId.toString().includes(searchTerm) ||
        item.changeType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.remark && item.remark.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.rejectRemark && item.rejectRemark.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Additional filters
    if (filters.spaceId) {
      filtered = filtered.filter(item => item.spaceId.toString().includes(filters.spaceId));
    }
    if (filters.userId) {
      filtered = filtered.filter(item => item.userId.toLowerCase().includes(filters.userId.toLowerCase()));
    }
    if (filters.changeType) {
      filtered = filtered.filter(item => item.changeType === filters.changeType);
    }
    if (filters.attachmentType) {
      filtered = filtered.filter(item => item.attachmentType === filters.attachmentType);
    }

    setFilteredItems(filtered);
  };

  const approveItem = async (item, remark = '') => {
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.update',
          payload: {
            id: item.id,
            data: {
              approve: true,
              reject: false,
              remark: remark,
              pushStatus: 'PUSHED'
            }
          }
        },
        label: 'approve checklist item'
      });

      toast({
        title: 'Success',
        description: 'Item approved successfully'
      });
      
      fetchChecklistItems();
      setSelectedItem(null);
      setApprovalRemark('');
    } catch (error) {
      console.error('Error approving item:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve item',
        variant: 'destructive'
      });
    }
  };

  const rejectItem = async (item, reason) => {
    if (!reason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive'
      });
      return;
    }

    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.update',
          payload: {
            id: item.id,
            data: {
              approve: false,
              reject: true,
              rejectRemark: reason,
              pushStatus: 'FAILED'
            }
          }
        },
        label: 'reject checklist item'
      });

      toast({
        title: 'Success',
        description: 'Item rejected successfully'
      });
      
      fetchChecklistItems();
      setSelectedItem(null);
      setRejectRemark('');
    } catch (error) {
      console.error('Error rejecting item:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject item',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (item) => {
    if (item.approve) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    } else if (item.reject) {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    } else if (item.pushStatus === 'PENDING') {
      return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    } else {
      return <Badge variant="outline">{item.pushStatus}</Badge>;
    }
  };

  const getChangeTypeIcon = (changeType) => {
    const icons = {
      ITEM_UPDATE: MessageSquare,
      ATTACHMENT_ADDED: Camera,
      APPROVAL_CHANGED: ThumbsUp,
      REMARK_ADDED: MessageSquare
    };
    return icons[changeType] || MessageSquare;
  };

  const getAttachmentTypeIcon = (attachmentType) => {
    if (attachmentType === 'IMAGE360') return Camera;
    if (attachmentType === 'TABLETIMAGE') return Smartphone;
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ChecklistItemCard = ({ item }) => {
    const ChangeIcon = getChangeTypeIcon(item.changeType);
    const AttachmentIcon = getAttachmentTypeIcon(item.attachmentType);

    return (
      <Card key={item.id} className="mb-4 hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <ChangeIcon className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="font-medium">
                  {item.changeType.replace('_', ' ')}
                  {AttachmentIcon && <AttachmentIcon className="w-4 h-4 inline-block ml-2 text-gray-500" />}
                </h3>
                <p className="text-sm text-gray-500">ID: {item.id}</p>
              </div>
            </div>
            {getStatusBadge(item)}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span>User: {item.userId}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>Space: {item.spaceId}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{formatDate(item.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>Priority: {item.priority}</span>
            </div>
          </div>

          {item.changeData && (
            <div className="mb-4">
              <strong className="text-sm">Changes:</strong>
              <pre className="bg-gray-50 p-2 rounded text-xs mt-1 overflow-x-auto max-h-20">
                {typeof item.changeData === 'string' 
                  ? item.changeData 
                  : JSON.stringify(item.changeData, null, 2)
                }
              </pre>
            </div>
          )}

          {item.remark && (
            <div className="mb-3 p-3 bg-green-50 rounded-lg">
              <strong className="text-sm text-green-800">Approval Remark:</strong>
              <p className="text-sm text-green-700 mt-1">{item.remark}</p>
            </div>
          )}

          {item.rejectRemark && (
            <div className="mb-3 p-3 bg-red-50 rounded-lg">
              <strong className="text-sm text-red-800">Rejection Reason:</strong>
              <p className="text-sm text-red-700 mt-1">{item.rejectRemark}</p>
            </div>
          )}

          {item.pushStatus === 'PENDING' && !item.approve && !item.reject && (
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Button
                size="sm"
                onClick={() => setSelectedItem(item)}
                className="bg-green-600 hover:bg-green-700"
              >
                <ThumbsUp className="w-3 h-3 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setSelectedItem({ ...item, action: 'reject' })}
              >
                <ThumbsDown className="w-3 h-3 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedItem({ ...item, action: 'view' })}
              >
                <Eye className="w-3 h-3 mr-1" />
                Details
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const pendingCount = checklistItems.filter(item => item.pushStatus === 'PENDING').length;
  const approvedCount = checklistItems.filter(item => item.approve === true).length;
  const rejectedCount = checklistItems.filter(item => item.reject === true).length;
  const attachmentCount = checklistItems.filter(item => item.changeType === 'ATTACHMENT_ADDED').length;

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Checklist Approval Workflow</h1>
        <Button onClick={fetchChecklistItems} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold">{pendingCount}</div>
            <div className="text-sm text-gray-600">Pending Approval</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold">{approvedCount}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{attachmentCount}</div>
            <div className="text-sm text-gray-600">Attachments</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4" />
            <h3 className="font-medium">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Input
              placeholder="Space ID"
              value={filters.spaceId}
              onChange={(e) => setFilters({...filters, spaceId: e.target.value})}
            />
            <Input
              placeholder="User ID"
              value={filters.userId}
              onChange={(e) => setFilters({...filters, userId: e.target.value})}
            />
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.changeType}
              onChange={(e) => setFilters({...filters, changeType: e.target.value})}
            >
              <option value="">All Change Types</option>
              <option value="ITEM_UPDATE">Item Update</option>
              <option value="ATTACHMENT_ADDED">Attachment Added</option>
              <option value="APPROVAL_CHANGED">Approval Changed</option>
              <option value="REMARK_ADDED">Remark Added</option>
            </select>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.attachmentType}
              onChange={(e) => setFilters({...filters, attachmentType: e.target.value})}
            >
              <option value="">All Attachment Types</option>
              <option value="IMAGE360">360° Images</option>
              <option value="TABLETIMAGE">Tablet Images</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">
            Pending ({filteredItems.filter(item => item.pushStatus === 'PENDING').length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({filteredItems.filter(item => item.approve === true).length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({filteredItems.filter(item => item.reject === true).length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All ({filteredItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="space-y-4">
            {filteredItems.filter(item => item.pushStatus === 'PENDING').length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No pending items for approval</p>
                </CardContent>
              </Card>
            ) : (
              filteredItems.filter(item => item.pushStatus === 'PENDING').map(item => (
                <ChecklistItemCard key={item.id} item={item} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          <div className="space-y-4">
            {filteredItems.filter(item => item.approve === true).map(item => (
              <ChecklistItemCard key={item.id} item={item} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rejected" className="mt-6">
          <div className="space-y-4">
            {filteredItems.filter(item => item.reject === true).map(item => (
              <ChecklistItemCard key={item.id} item={item} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="space-y-4">
            {filteredItems.map(item => (
              <ChecklistItemCard key={item.id} item={item} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Approval/Rejection Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">
                  {selectedItem.action === 'reject' ? 'Reject Item' : 
                   selectedItem.action === 'view' ? 'Item Details' : 'Approve Item'}
                </h2>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedItem(null)}
                >
                  Close
                </Button>
              </div>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="font-medium">Change Type:</label>
                  <p className="text-sm">{selectedItem.changeType.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="font-medium">User:</label>
                  <p className="text-sm">{selectedItem.userId}</p>
                </div>
                <div>
                  <label className="font-medium">Space ID:</label>
                  <p className="text-sm">{selectedItem.spaceId}</p>
                </div>
                {selectedItem.changeData && (
                  <div>
                    <label className="font-medium">Changes:</label>
                    <pre className="text-xs bg-gray-100 p-4 rounded overflow-x-auto">
                      {typeof selectedItem.changeData === 'string' 
                        ? selectedItem.changeData 
                        : JSON.stringify(selectedItem.changeData, null, 2)
                      }
                    </pre>
                  </div>
                )}
              </div>

              {selectedItem.action === 'reject' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Rejection Reason *
                    </label>
                    <Textarea
                      value={rejectRemark}
                      onChange={(e) => setRejectRemark(e.target.value)}
                      placeholder="Please provide a clear reason for rejection..."
                      rows={4}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => rejectItem(selectedItem, rejectRemark)}
                      disabled={!rejectRemark.trim()}
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Reject Item
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedItem(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : selectedItem.action === 'view' ? (
                <div>
                  <pre className="text-xs bg-gray-100 p-4 rounded overflow-x-auto">
                    {JSON.stringify(selectedItem, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Approval Remark (Optional)
                    </label>
                    <Textarea
                      value={approvalRemark}
                      onChange={(e) => setApprovalRemark(e.target.value)}
                      placeholder="Add any remarks about the approval..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => approveItem(selectedItem, approvalRemark)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Approve Item
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedItem(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistApprovalWorkflow;

